import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';

// Use existing table structures
interface ServiceUserCommissionRate {
  id: string;
  organization_id: string;
  service_id: string; // REQUIRED - not optional!
  user_id: string;
  commission_rate: number; // Renamed from custom_percentage
  is_active: boolean;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    full_name: string;
    email: string;
  };
  // Additional fields for group-inherited commissions
  is_from_group?: boolean; // true if this commission comes from a group assignment
  group_id?: string;
  group_name?: string;
  group_color?: string;
}

interface ServiceReferrerCommissionRate {
  id: string;
  organization_id: string;
  service_id: string;
  referrer_id: string;
  commission_type: string;
  commission_value: number;
  created_at: string;
  updated_at: string;
  referrer?: {
    id: string;
    full_name: string;
    email: string;
  };
}

interface ServiceCommissionSettings {
  service_id: string;
  enable_commission_scaling: boolean;
}

interface CommissionGroup {
  id: string;
  name: string;
  description?: string;
  base_percentage: number;
  fixed_amount?: number;
  color: string;
  is_active: boolean;
  organization_id: string;
  created_at: string;
  updated_at: string;
  user_count?: number;
}

interface GroupUser {
  id: string;
  full_name: string;
  email: string;
  commission_rate?: number;
}

interface ReferrerCommissionGroup {
  id: string;
  name: string;
  description?: string;
  base_percentage: number;
  fixed_amount?: number;
  color: string;
  is_active: boolean;
  organization_id: string;
  created_at: string;
  updated_at: string;
  referrer_count?: number;
}

interface ReferrerGroupMember {
  id: string;
  full_name: string;
  email?: string;
  commission_rate?: number;
}

interface ServiceCommissionStore {
  userRates: ServiceUserCommissionRate[];
  referrerRates: ServiceReferrerCommissionRate[];
  commissionGroups: CommissionGroup[];
  groupUsers: GroupUser[];
  referrerCommissionGroups: ReferrerCommissionGroup[];
  referrerGroupMembers: ReferrerGroupMember[];
  loading: boolean;
  error: string | null;

  // User commission rates
  fetchUserRates: (serviceId?: string) => Promise<void>;
  createUserRate: (data: Omit<ServiceUserCommissionRate, 'id' | 'created_at' | 'updated_at'>) => Promise<boolean>;
  updateUserRate: (id: string, data: Partial<ServiceUserCommissionRate>) => Promise<boolean>;
  deleteUserRate: (id: string) => Promise<boolean>;

  // Referrer commission rates
  fetchReferrerRates: (serviceId?: string) => Promise<void>;
  createReferrerRate: (data: Omit<ServiceReferrerCommissionRate, 'id' | 'created_at' | 'updated_at'>) => Promise<boolean>;
  updateReferrerRate: (id: string, data: Partial<ServiceReferrerCommissionRate>) => Promise<boolean>;
  deleteReferrerRate: (id: string) => Promise<boolean>;

  // Commission groups
  fetchCommissionGroups: () => Promise<void>;
  fetchGroupUsers: (groupId: string) => Promise<void>;
  applyGroupToService: (serviceId: string, groupId: string, commissionRate: number) => Promise<{ success: boolean; count: number }>;

  // Referrer commission groups
  fetchReferrerCommissionGroups: () => Promise<void>;
  fetchReferrerGroupMembers: (groupId: string) => Promise<void>;
  applyReferrerGroupToService: (serviceId: string, groupId: string, commissionRate: number) => Promise<{ success: boolean; count: number }>;

  // Service commission settings
  updateServiceCommissionSettings: (serviceId: string, settings: Partial<ServiceCommissionSettings>) => Promise<boolean>;
  getEffectiveUserRate: (serviceId: string, userId: string) => Promise<number>;
  getEffectiveReferrerRate: (serviceId: string, referrerId: string) => Promise<number>;
  getScaledUserRate: (serviceId: string, userId: string, referrerRate?: number) => Promise<number>;
}

export const useServiceCommissionStore = create<ServiceCommissionStore>((set, get) => ({
  userRates: [],
  referrerRates: [],
  commissionGroups: [],
  groupUsers: [],
  referrerCommissionGroups: [],
  referrerGroupMembers: [],
  loading: false,
  error: null,

  fetchUserRates: async (serviceId?: string) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // FIXED: Get service-specific commission rates from correct table
      // Specify the foreign key explicitly to avoid ambiguity
      let query = supabase
        .from('service_commission_rates')
        .select(`
          *,
          user:user_profiles!service_commission_rates_user_id_fkey (
            id,
            full_name,
            email
          )
        `)
        .eq('organization_id', userProfile.organization_id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      // Filter by service_id if provided
      if (serviceId) {
        query = query.eq('service_id', serviceId);
      }

      const { data: explicitRates, error } = await query;

      if (error) throw error;

      const allRates: ServiceUserCommissionRate[] = explicitRates || [];

      // If a specific service is requested, also fetch users who inherit commission from groups
      if (serviceId) {
        // Find all explicit user IDs to avoid duplicates
        const explicitUserIds = new Set(allRates.map(r => r.user_id));

        // Find all users in groups that have commissions for this service
        // by checking which users belong to groups whose members have service_commission_rates
        const { data: groupCommissions, error: groupError } = await supabase
          .from('service_commission_rates')
          .select(`
            commission_rate,
            user:user_profiles!service_commission_rates_user_id_fkey (
              commission_group_id
            )
          `)
          .eq('organization_id', userProfile.organization_id)
          .eq('service_id', serviceId)
          .eq('is_active', true)
          .not('user.commission_group_id', 'is', null);

        if (!groupError && groupCommissions && groupCommissions.length > 0) {
          // Get unique group IDs and their commission rates from this service
          const groupCommissionMap = new Map<string, number>();

          for (const gc of groupCommissions) {
            const groupId = gc.user?.commission_group_id;
            if (groupId && !groupCommissionMap.has(groupId)) {
              groupCommissionMap.set(groupId, gc.commission_rate);
            }
          }

          // For each group, get ALL users in that group
          for (const [groupId, commissionRate] of groupCommissionMap.entries()) {
            const { data: groupData } = await supabase
              .from('commission_groups')
              .select('name, color')
              .eq('id', groupId)
              .single();

            const { data: groupUsers } = await supabase
              .from('user_profiles')
              .select('id, full_name, email')
              .eq('commission_group_id', groupId)
              .eq('organization_id', userProfile.organization_id)
              .eq('active', true);

            if (groupUsers) {
              for (const user of groupUsers) {
                // Only add if not already in explicit rates
                if (!explicitUserIds.has(user.id)) {
                  allRates.push({
                    id: `group-${groupId}-${user.id}`, // Virtual ID for group-inherited rates
                    organization_id: userProfile.organization_id,
                    service_id: serviceId,
                    user_id: user.id,
                    commission_rate: commissionRate,
                    is_active: true,
                    created_at: '',
                    updated_at: '',
                    user: {
                      id: user.id,
                      full_name: user.full_name,
                      email: user.email
                    },
                    is_from_group: true,
                    group_id: groupId,
                    group_name: groupData?.name || 'Gruppo sconosciuto',
                    group_color: groupData?.color || '#6B7280'
                  });
                }
              }
            }
          }
        }
      }

      set({ userRates: allRates });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createUserRate: async (data) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // FIXED: Insert into service_commission_rates table (service-specific rates)
      if (!data.service_id) throw new Error('service_id is required for service-specific commission rates');

      // Use UPSERT to handle existing records (update if exists, insert if not)
      const { error } = await supabase
        .from('service_commission_rates')
        .upsert({
          user_id: data.user_id,
          service_id: data.service_id,
          commission_rate: data.commission_rate,
          organization_id: userProfile.organization_id,
          is_active: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'service_id,user_id',
          ignoreDuplicates: false
        });

      if (error) throw error;

      // Refresh data
      await get().fetchUserRates(data.service_id);
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  updateUserRate: async (id, data) => {
    try {
      set({ loading: true, error: null });

      // FIXED: Update service_commission_rates table
      const updateData: any = {};
      if (data.commission_rate !== undefined) updateData.commission_rate = data.commission_rate;
      if (data.is_active !== undefined) updateData.is_active = data.is_active;

      const { error } = await supabase
        .from('service_commission_rates')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      // Refresh data (pass service_id if available from data)
      await get().fetchUserRates(data.service_id);
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  deleteUserRate: async (id) => {
    try {
      set({ loading: true, error: null });

      // FIXED: Deactivate the rate in service_commission_rates table
      const { error } = await supabase
        .from('service_commission_rates')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      // Refresh data
      await get().fetchUserRates();
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  fetchReferrerRates: async (serviceId?: string) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      let query = supabase
        .from('referrer_service_commissions')
        .select(`
          *,
          referrer:referrers!referrer_service_commissions_referrer_id_fkey (
            id,
            full_name,
            email
          )
        `)
        .eq('organization_id', userProfile.organization_id)
        .eq('commission_type', 'percentage')
        .order('created_at', { ascending: false });

      if (serviceId) {
        query = query.eq('service_id', serviceId);
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ referrerRates: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createReferrerRate: async (data) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      const { error } = await supabase
        .from('referrer_service_commissions')
        .insert({
          organization_id: userProfile.organization_id,
          referrer_id: data.referrer_id,
          service_id: data.service_id,
          commission_type: 'percentage',
          commission_value: data.commission_rate
        });

      if (error) throw error;

      // Refresh data
      await get().fetchReferrerRates();
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  updateReferrerRate: async (id, data) => {
    try {
      set({ loading: true, error: null });

      const { error } = await supabase
        .from('referrer_service_commissions')
        .update({
          commission_value: data.commission_rate,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Refresh data
      await get().fetchReferrerRates();
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  deleteReferrerRate: async (id) => {
    try {
      set({ loading: true, error: null });

      const { error } = await supabase
        .from('referrer_service_commissions')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh data
      await get().fetchReferrerRates();
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  updateServiceCommissionSettings: async (serviceId, settings) => {
    try {
      set({ loading: true, error: null });

      const { error } = await supabase
        .from('services')
        .update({
          enable_commission_scaling: settings.enable_commission_scaling,
          updated_at: new Date().toISOString()
        })
        .eq('id', serviceId);

      if (error) throw error;

      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  // P12: preview rate via resolve_commission_rate — la STESSA gerarchia usata dal motore in
  // flusia-core (deal override → rate servizio-utente → gruppo → default utente → default org).
  // Le vecchie RPC get_service_user_commission_rate / get_service_referrer_commission_rate /
  // get_scaled_user_commission_rate sono state dismesse col cutover V2.
  getEffectiveUserRate: async (serviceId, userId) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      const { data, error } = await supabase.rpc('resolve_commission_rate', {
        p_organization_id: userProfile.organization_id,
        p_deal_id: null,
        p_service_id: serviceId,
        p_person_id: userId,
        p_person_type: 'user'
      });

      if (error) throw error;
      return data?.[0]?.rate ?? 0;
    } catch (error) {
      console.error('Error getting effective user rate:', error);
      return 0;
    }
  },

  getEffectiveReferrerRate: async (serviceId, referrerId) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      const { data, error } = await supabase.rpc('resolve_commission_rate', {
        p_organization_id: userProfile.organization_id,
        p_deal_id: null,
        p_service_id: serviceId,
        p_person_id: referrerId,
        p_person_type: 'referrer'
      });

      if (error) throw error;
      return data?.[0]?.rate ?? 0;
    } catch (error) {
      console.error('Error getting effective referrer rate:', error);
      return 0;
    }
  },

  getScaledUserRate: async (serviceId, userId, referrerRate = 0) => {
    // Decurtazione: rate agente − retrocessione segnalatore, mai sotto zero (stessa regola del motore)
    const baseRate = await get().getEffectiveUserRate(serviceId, userId);
    return Math.max(0, baseRate - (referrerRate || 0));
  },

  fetchCommissionGroups: async () => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Fetch commission groups
      const { data: groupsData, error: groupsError } = await supabase
        .from('commission_groups')
        .select('*')
        .eq('organization_id', userProfile.organization_id)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (groupsError) throw groupsError;

      // Count users for each group
      const groupsWithCount = await Promise.all(
        (groupsData || []).map(async (group) => {
          const { count } = await supabase
            .from('user_profiles')
            .select('*', { count: 'exact', head: true })
            .eq('commission_group_id', group.id)
            .eq('organization_id', userProfile.organization_id)
            .eq('active', true);

          return {
            ...group,
            user_count: count || 0
          };
        })
      );

      set({ commissionGroups: groupsWithCount });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  fetchGroupUsers: async (groupId: string) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Fetch users in this group
      const { data: users, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, commission_rate')
        .eq('commission_group_id', groupId)
        .eq('organization_id', userProfile.organization_id)
        .eq('active', true)
        .order('full_name', { ascending: true });

      if (error) throw error;

      set({ groupUsers: users || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  applyGroupToService: async (serviceId: string, groupId: string, commissionRate: number) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // NUOVO: Salva l'associazione gruppo-servizio in service_group_commissions
      const { error: groupServiceError } = await supabase
        .from('service_group_commissions')
        .upsert({
          service_id: serviceId,
          commission_group_id: groupId,
          commission_rate: commissionRate,
          organization_id: userProfile.organization_id,
          is_active: true,
          updated_at: new Date().toISOString(),
          created_by: userData.user.id
        }, {
          onConflict: 'service_id,commission_group_id',
          ignoreDuplicates: false
        });

      if (groupServiceError) throw groupServiceError;

      // Fetch users in this group
      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('commission_group_id', groupId)
        .eq('organization_id', userProfile.organization_id)
        .eq('active', true);

      if (usersError) throw usersError;

      if (!users || users.length === 0) {
        // Anche senza utenti, l'associazione gruppo-servizio è stata salvata
        // per gestire futuri utenti aggiunti al gruppo
        return { success: true, count: 0 };
      }

      // Apply commission rate to all users in the group for this service
      let successCount = 0;

      for (const user of users) {
        const { error: upsertError } = await supabase
          .from('service_commission_rates')
          .upsert({
            user_id: user.id,
            service_id: serviceId,
            commission_rate: commissionRate,
            organization_id: userProfile.organization_id,
            is_active: true,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'service_id,user_id',
            ignoreDuplicates: false
          });

        if (!upsertError) {
          successCount++;
        }
      }

      // Refresh user rates
      await get().fetchUserRates(serviceId);

      return { success: true, count: successCount };
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return { success: false, count: 0 };
    } finally {
      set({ loading: false });
    }
  },

  fetchReferrerCommissionGroups: async () => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Fetch referrer groups (nuovo schema referrer_groups, mappato alla shape legacy usata dai consumer)
      const { data: groupsData, error: groupsError } = await supabase
        .from('referrer_groups')
        .select('*')
        .eq('organization_id', userProfile.organization_id)
        .eq('is_active', true)
        .order('group_name', { ascending: true });

      if (groupsError) throw groupsError;

      // Count referrers for each group
      const groupsWithCount = await Promise.all(
        (groupsData || []).map(async (group) => {
          const { count } = await supabase
            .from('referrers')
            .select('*', { count: 'exact', head: true })
            .eq('referrer_group_id', group.id)
            .eq('organization_id', userProfile.organization_id);

          return {
            id: group.id,
            name: group.group_name,
            description: group.group_description || undefined,
            base_percentage: group.default_commission_percentage,
            color: group.color,
            is_active: group.is_active,
            organization_id: group.organization_id,
            created_at: group.created_at,
            updated_at: group.updated_at,
            referrer_count: count || 0
          };
        })
      );

      set({ referrerCommissionGroups: groupsWithCount });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  fetchReferrerGroupMembers: async (groupId: string) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Fetch referrers in this group
      const { data: referrers, error } = await supabase
        .from('referrers')
        .select('id, full_name, email, commission_rate')
        .eq('referrer_group_id', groupId)
        .eq('organization_id', userProfile.organization_id)
        .order('full_name', { ascending: true });

      if (error) throw error;

      set({ referrerGroupMembers: referrers || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  applyReferrerGroupToService: async (serviceId: string, groupId: string, commissionRate: number) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Fetch tutti i segnalatori del gruppo
      const { data: referrers, error: referrersError } = await supabase
        .from('referrers')
        .select('id')
        .eq('referrer_group_id', groupId)
        .eq('organization_id', userProfile.organization_id);

      if (referrersError) throw referrersError;

      if (!referrers || referrers.length === 0) {
        return { success: true, count: 0 };
      }

      // Applica la commissione a ogni segnalatore del gruppo
      // usando referrer_id (NON referrer_group_id che ha FK sbagliato)
      let successCount = 0;

      for (const referrer of referrers) {
        // Check se esiste già un record per questo segnalatore+servizio
        const { data: existing } = await supabase
          .from('referrer_service_commissions')
          .select('id')
          .eq('referrer_id', referrer.id)
          .eq('service_id', serviceId)
          .eq('organization_id', userProfile.organization_id)
          .maybeSingle();

        let upsertError;
        if (existing) {
          // Update existing
          const { error } = await supabase
            .from('referrer_service_commissions')
            .update({
              commission_value: commissionRate,
              commission_type: 'percentage',
              updated_at: new Date().toISOString()
            })
            .eq('id', existing.id);
          upsertError = error;
        } else {
          // Insert new
          const { error } = await supabase
            .from('referrer_service_commissions')
            .insert({
              referrer_id: referrer.id,
              service_id: serviceId,
              commission_type: 'percentage',
              commission_value: commissionRate,
              organization_id: userProfile.organization_id
            });
          upsertError = error;
        }

        if (!upsertError) {
          successCount++;
        }
      }

      // Refresh referrer rates
      await get().fetchReferrerRates(serviceId);

      return { success: true, count: successCount };
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return { success: false, count: 0 };
    } finally {
      set({ loading: false });
    }
  }
}));